'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';
import { createCharacter } from './createCharacter';
import { generatePortrait } from './generatePortrait';

/** A character the child invented during a voice session. */
export interface VoiceCharacterInput {
  name: string;
  description?: string;
}

export interface SaveVoiceCharactersPayload {
  /** Characters persisted to the family library, with their new ids. */
  created: Array<{ name: string; id: string }>;
  /** Names that could not be saved (e.g. content-safety block) — the caller
   *  folds these back into the prompt so nothing the child said is lost. */
  failed: string[];
}

/**
 * Persist the characters a child invented mid voice-session into the family
 * library and kick off a neutral master-reference portrait for each (async in
 * the worker — fake on dev). Returns the created ids so the wizard can add them
 * to ContentParameters.characterIds and REUSE them, exactly like a hero picked
 * from the library. Portrait generation is best-effort: a failed enqueue never
 * drops the saved character.
 */
export async function saveVoiceCharacters(
  characters: VoiceCharacterInput[],
): Promise<ActionResult<SaveVoiceCharactersPayload>> {
  try {
    const created: SaveVoiceCharactersPayload['created'] = [];
    const failed: string[] = [];

    for (const c of characters) {
      const name = c.name?.trim();
      if (!name) continue;
      const description = c.description?.trim() || '';

      const res = await createCharacter({
        name,
        physicalDescription: description,
        // Reuse-by-name later: the agent + picker match on name/synonyms.
        synonyms: [],
      });
      if (!res.success || !res.data) {
        failed.push(name);
        continue;
      }
      created.push({ name, id: res.data.id });

      // Best-effort portrait — the character is already saved regardless.
      try {
        await generatePortrait({
          characterId: res.data.id,
          customDescription: description || undefined,
        });
      } catch {
        /* portrait deferred; character stands on its own */
      }
    }

    return { success: true, data: { created, failed } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('saveVoiceCharacters', e) };
  }
}
